import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApi, messages as messagesApi, forSale as forSaleApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmt, getDriftwoodWeekDates, bedroomCategory, effectiveViewType } from '../utils/forSale';
import ForSaleFilterPanel, { DEFAULT_FORSALE_FILTERS } from './ForSaleFilterPanel';
import ListForSaleModal from './ListForSaleModal';

// ── Contact Owner Modal ──────────────────────────────────────────────────────
// "Make an Offer" opens an on-platform message thread with the unit's owner
// instead of emailing the resort, so the owner's contact info stays private.
export function ContactOwnerModal({ listing, propertyName, onClose }) {
  const { user } = useAuth();
  const isOwnListing = !!user && listing.owner_id === user.id;
  const { data, loading, refetch } = useApi(user && !isOwnListing ? `/messages/for-sale/${listing.id}` : null);
  const msgs = data?.messages || [];
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await messagesApi.sendForSale(listing.id, text.trim());
      setText('');
      refetch();
    } finally {
      setSending(false);
    }
  }

  const backdrop = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  };
  const box = {
    background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '2rem',
    maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  };
  const input = {
    width: '100%', padding: '0.6rem 0.8rem', minHeight: 44, border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-md)', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, margin: 0 }}>Contact the Owner</h2>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', margin: '0.2rem 0 0' }}>
              Unit {listing.unit_number} · Week {listing.week_number} · {listing.room_type}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--text-xl)', cursor: 'pointer', color: 'var(--ink-muted)', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: 'var(--text-sm)' }}>
          <span style={{ fontWeight: 600 }}>{propertyName}</span> &nbsp;·&nbsp; Asking: <strong>{fmt(listing.asking_price_cents)}</strong>
        </div>

        {isOwnListing ? (
          <p style={{ color: 'var(--ink-muted)' }}>This is your own listing.</p>
        ) : !user ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ color: 'var(--ink-muted)', marginBottom: '1rem' }}>
              Log in to message the owner directly. Your contact info stays private until you choose to share it.
            </p>
            <Link to="/login" className="btn btn-primary btn-full">Log In</Link>
          </div>
        ) : (
          <>
            {loading ? (
              <p style={{ color: 'var(--ink-muted)' }}>Loading…</p>
            ) : msgs.length > 0 && (
              <div style={{ maxHeight: 260, overflowY: 'auto', padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', marginBottom: '0.75rem' }}>
                {msgs.map(m => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_id === user.id ? 'flex-end' : 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{
                      maxWidth: '80%', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)',
                      background: m.sender_id === user.id ? 'var(--brand)' : 'var(--surface)',
                      color: m.sender_id === user.id ? 'var(--surface)' : 'var(--ink)',
                      border: m.sender_id === user.id ? 'none' : '1px solid var(--border)',
                    }}>
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={send} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                style={{ ...input, flex: 1 }}
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Ask about this unit or make an offer…"
              />
              <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()} style={{ minHeight: 44, padding: '0 1.25rem' }}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </form>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', marginTop: '0.75rem', textAlign: 'center' }}>
              Messages stay on ShareTime — the owner's contact info is never shared directly.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const TRANSFER_TOOLTIP_TEXT = "This owner is looking to transfer ownership — you'd take over the annual maintenance fee going forward instead of paying a purchase price.";

// ── Unit type icon (generic door/key illustration) ─────────────────────────
function UnitIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--brand-dark)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 21V5a2 2 0 012-2h4l6 4v14" />
      <path d="M6 21h12M12 7v14" />
      <circle cx="9.5" cy="14" r="0.9" fill="var(--brand-dark)" stroke="none" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="3.2" />
      <path strokeLinecap="round" d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
    </svg>
  );
}

// ── Transfer Ownership badge — amber pill with a click/hover tooltip ────────
function TransferBadge() {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="transfer-badge"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="transfer-badge-btn"
      >
        Transfer Ownership
      </button>
      {open && (
        <span role="tooltip" className="transfer-badge-tooltip">
          {TRANSFER_TOOLTIP_TEXT}
        </span>
      )}
    </span>
  );
}

function ForSaleCard({ listing, descriptionsByUnit = {}, onOffer, onRemove, showProperty = false }) {
  const { user } = useAuth();
  const desc = descriptionsByUnit[listing.unit_number];
  const isTransfer = listing.asking_price_cents < 1000;
  const isOcean = listing.view_type?.toLowerCase().includes('ocean');
  const isOwn = !!user && listing.owner_id === user.id;

  return (
    <div className="card for-sale-card" onClick={() => !isOwn && onOffer(listing)}>
      {showProperty && (
        <div className="for-sale-card-property">
          {listing.property_name}{listing.property_location ? ` · ${listing.property_location}` : ''}
        </div>
      )}

      <div className="for-sale-card-top">
        <div className="for-sale-card-icon"><UnitIcon /></div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="for-sale-card-unit">
            Unit {listing.unit_number}
            {listing.view_type && (
              <span className={`view-badge ${isOcean ? 'view-badge-ocean' : ''}`}>{listing.view_type}</span>
            )}
          </div>
          <div className="for-sale-card-week">
            Week {listing.week_number} · {getDriftwoodWeekDates(listing.unit_number, listing.week_number, 2026)} (2026)
          </div>
        </div>
      </div>

      <div className="for-sale-card-roomtype">
        {listing.room_type}{desc?.label && desc.label !== listing.room_type ? ` — ${desc.label}` : ''}
      </div>
      {(listing.description || desc?.description) && (
        <p className="for-sale-card-desc">{listing.description || desc?.description}</p>
      )}

      <div className="for-sale-card-guests">
        <PersonIcon /> Sleeps up to {listing.max_occupancy}
      </div>

      <div className="for-sale-card-price-block">
        <div className="for-sale-card-price">{fmt(listing.asking_price_cents)}</div>
        <div className="for-sale-card-maint">{fmt(listing.maintenance_fee_cents)}/yr maintenance fee</div>
        {isTransfer && <TransferBadge />}
      </div>

      {isOwn ? (
        <button className="for-sale-card-cta" onClick={e => { e.stopPropagation(); onRemove?.(listing); }}>
          Remove Listing
        </button>
      ) : (
        <button className="for-sale-card-cta" onClick={e => { e.stopPropagation(); onOffer(listing); }}>
          Make an Offer
        </button>
      )}
    </div>
  );
}

// ── Card grid: used by both the single-property ForSaleListings section and
// the cross-property "For Sale" browse tab. Cards on every screen size. ─────
export function ForSaleGrid({ listings, descriptionsByUnit = {}, onOffer, onRemove, showProperty = false }) {
  if (listings.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', color: 'var(--ink-faint)' }}>
        No listings match your filters.
      </div>
    );
  }

  return (
    <div className="for-sale-cards">
      {listings.map(l => (
        <ForSaleCard key={l.id} listing={l} descriptionsByUnit={descriptionsByUnit} onOffer={onOffer} onRemove={onRemove} showProperty={showProperty} />
      ))}
    </div>
  );
}

// ── For-Sale Listings (card grid) ────────────────────────────────────────────
export default function ForSaleListings({ propertyId, propertyName }) {
  const { user } = useAuth();
  const { data, loading, refetch } = useApi(`/properties/${propertyId}/for-sale`);
  const { data: descData } = useApi(`/properties/${propertyId}/room-descriptions`);
  const forSaleListings    = useMemo(() => data?.listings || [], [data]);
  const descriptionsByUnit = useMemo(() => descData?.byUnit || {}, [descData]);

  const [filters, setFilters] = useState(DEFAULT_FORSALE_FILTERS);
  const [offerTarget, setOfferTarget] = useState(null);
  const [showListForm, setShowListForm] = useState(false);

  const filtered = useMemo(() => {
    let rows = forSaleListings.slice();
    if (filters.bedroom)  rows = rows.filter(l => bedroomCategory(l.room_type) === filters.bedroom);
    if (filters.viewType) rows = rows.filter(l => effectiveViewType(l, descriptionsByUnit).toLowerCase().includes(filters.viewType));
    if (filters.priceMin) rows = rows.filter(l => l.asking_price_cents >= parseFloat(filters.priceMin) * 100);
    if (filters.priceMax) rows = rows.filter(l => l.asking_price_cents <= parseFloat(filters.priceMax) * 100);
    if (filters.maintMin) rows = rows.filter(l => l.maintenance_fee_cents >= parseFloat(filters.maintMin) * 100);
    if (filters.maintMax) rows = rows.filter(l => l.maintenance_fee_cents <= parseFloat(filters.maintMax) * 100);
    if (filters.weekFrom) rows = rows.filter(l => l.week_number >= parseInt(filters.weekFrom, 10));
    if (filters.weekTo)   rows = rows.filter(l => l.week_number <= parseInt(filters.weekTo, 10));
    rows.sort((a, b) => {
      if (filters.sortBy === 'price_asc')  return a.asking_price_cents - b.asking_price_cents;
      if (filters.sortBy === 'price_desc') return b.asking_price_cents - a.asking_price_cents;
      if (filters.sortBy === 'maint_asc')  return a.maintenance_fee_cents - b.maintenance_fee_cents;
      return a.week_number - b.week_number; // 'week'
    });
    return rows;
  }, [forSaleListings, descriptionsByUnit, filters]);

  async function handleRemove(listing) {
    if (!window.confirm(`Remove your listing for Unit ${listing.unit_number}?`)) return;
    await forSaleApi.remove(listing.id);
    refetch();
  }

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        {user ? (
          <button className="btn btn-primary" onClick={() => setShowListForm(true)}>
            + List Your Unit For Sale
          </button>
        ) : (
          <Link to="/login" className="btn btn-primary">
            + List Your Unit For Sale
          </Link>
        )}
      </div>

      {showListForm && (
        <ListForSaleModal
          propertyId={propertyId}
          onClose={() => setShowListForm(false)}
          onSuccess={() => { setShowListForm(false); refetch(); }}
        />
      )}

      {/* Explainer for buyers unfamiliar with timeshare weeks */}
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: 720 }}>
        Buying a timeshare week means purchasing the right to stay in a specific unit during the same week every year,
        directly from the current owner. You pay a one-time purchase price plus the resort's annual maintenance fee going
        forward — no financing through ShareTime and no transaction fee. Prices are negotiable; message the owner directly
        using the button on each listing below.
      </p>

      {loading ? (
        <p style={{ color: 'var(--ink-muted)' }}>Loading for-sale listings…</p>
      ) : forSaleListings.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', color: 'var(--ink-faint)' }}>
          No units currently listed for sale at this resort.
        </div>
      ) : (
        <>
          <ForSaleFilterPanel
            filters={filters}
            onChange={setFilters}
            listings={forSaleListings}
            resultCount={filtered.length}
          />

          <ForSaleGrid listings={filtered} descriptionsByUnit={descriptionsByUnit} onOffer={setOfferTarget} onRemove={handleRemove} />
        </>
      )}

      {offerTarget && (
        <ContactOwnerModal listing={offerTarget} propertyName={propertyName} onClose={() => setOfferTarget(null)} />
      )}
    </div>
  );
}
